import { useCallback, useEffect, useState } from "react";

import { Spinner, Form, InputGroup } from "react-bootstrap";
import { api, type FilesSortOptions } from "../../utils/api";

import { useDebounce } from "../../utils/useDebounce";
import { ErrorMessage } from "../../authorization/ErrorMessage";
import type z from "zod";
import { schemas } from "../../api/generated";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { DeletionModal as DeletionModal } from "../modals/DeletionModal";
import { FileAssignmentModal } from "../modals/FileAssignmentModal";
import { FileUploadInputGroup } from "../FileUploadInputGroup";
import { FilesTable } from "../tables/FilesTable";
import type { PaginationState, SortingState } from "@tanstack/react-table";

type FileDTO = z.infer<typeof schemas.FileWithUsage>;

function Files({ token }: { token: string }) {
  const [files, setFiles] = useState<FileDTO[]>([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileDTO | undefined>();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: "fileName", desc: false },
  ]);

  const [dataTimestamp, setDataTimestamp] = useState(0);

  useEffect(() => {
    // setIsLoading(true); // FUTURE: Reenable this after improving the loading UX so that the whole page isn't swapped out
    api
      .getFiles(
        token,
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        sorting.length > 0
          ? ((sorting[0]?.desc
              ? `${sorting[0].id}_desc`
              : `${sorting[0].id}_asc`) as FilesSortOptions)
          : undefined,
        debouncedSearchTerm || undefined
      )
      .then((body) => {
        const foundFiles = body.data;
        setFiles(foundFiles);
        setTotalFiles(body.metadata.totalCount);

        // Show error message only if there are no files at all without filters
        setErrorMessage(
          !debouncedSearchTerm && (!foundFiles || foundFiles.length === 0)
            ? "No data available!"
            : ""
        );
        return foundFiles;
      })
      .catch(() => {
        setErrorMessage("Failed to load files");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, debouncedSearchTerm, sorting, pagination, dataTimestamp]);

  const handleFileUploaded = useCallback(() => {
    setDataTimestamp(Date.now()); // Force refresh of data
  }, []);

  const handleFileAssigned = useCallback(() => {
    setSelectedFile(undefined);
    setDataTimestamp(Date.now()); // Force refresh of data
  }, []);

  const handleFileDeleted = useCallback(async (deletedFile: FileDTO) => {
    setIsLoading(true);
    setFiles((prevData) =>
      prevData.filter((file) => file.id !== deletedFile?.id)
    );
    setSelectedFile(undefined);
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  }

  return (
    <div>
      <div
        className="mb-2"
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
        }}
      >
        <FileUploadInputGroup
          token={token}
          onFileUploaded={handleFileUploaded}
        />
      </div>
      <InputGroup className="mb-3">
        <InputGroup.Text>
          <label htmlFor="search-files" title="Search files">
            <FontAwesomeIcon icon={faSearch} />
          </label>
        </InputGroup.Text>
        <Form.Control
          id="search-files"
          type="text"
          placeholder="Search files"
          value={searchTerm}
          maxLength={255}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      {(errorMessage && <ErrorMessage message={errorMessage} />) || (
        <FilesTable
          files={files}
          totalFiles={totalFiles}
          onAssign={(file) => {
            setSelectedFile(file);
            setShowAssignmentModal(true);
          }}
          onDelete={(file) => {
            // setSelectedUserFile({ fileId: file.id, userId: undefined });
            setSelectedFile(file);
            setShowDeletionModal(true);
          }}
          onTableChange={(pagination, sorting) => {
            setPagination(pagination);
            setSorting(sorting);
          }}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          sortBy={sorting?.[0]?.id}
          sortDescending={sorting?.[0]?.desc}
        />
      )}

      <FileAssignmentModal
        showAssignmentModal={showAssignmentModal}
        setShowAssignmentModal={setShowAssignmentModal}
        selectedFile={selectedFile}
        token={token}
        onFileAssigned={handleFileAssigned}
      />

      <DeletionModal
        showDeletionModal={showDeletionModal}
        setShowDeletionModal={setShowDeletionModal}
        token={token}
        selectedFile={selectedFile}
        onDeleted={handleFileDeleted}
      />
    </div>
  );
}

export default Files;
